(() => {
  const MARKER_SELECTOR = "[data-wt-benefits-draw]";
  const SECTION_IN_VIEW_CLASS = "is-in-view";
  const DATA_DONE_ATTR = "wtBenefitsInView";
  const STEPS_SELECTOR = ".benefits--steps";
  const ITEM_SELECTOR = ".benefits--steps .benefits-item";
  const CONNECTOR_SELECTOR = ".benefits-steps-connector__fill";
  const CONNECTOR_MASK_RECT_SELECTOR = ".benefits-steps-connector__mask";
  const STEP_PILL_SELECTOR = ".benefits-item-step";
  const STEP_PILL_FILL_VAR = "--benefits-step-fill";

  function getSectionsFromRoot(root = document) {
    const markers = Array.from(root.querySelectorAll(MARKER_SELECTOR));
    return markers
      .map((marker) => marker.closest('section[id^="shopify-section-"]'))
      .filter(Boolean);
  }

  function clamp01(n) {
    return Math.min(1, Math.max(0, n));
  }

  function setupStepsScrub(sectionEl) {
    const stepsRoot = sectionEl.querySelector(STEPS_SELECTOR);
    if (!stepsRoot) return () => {};

    const items = Array.from(sectionEl.querySelectorAll(ITEM_SELECTOR));
    if (items.length < 2) return () => {};

    const stepPills = items
      .map((item) => item.querySelector(STEP_PILL_SELECTOR))
      .filter(Boolean);

    const connectors = items
      .slice(0, -1)
      .map((item) => item.querySelector(CONNECTOR_SELECTOR))
      .filter(Boolean);
    if (!connectors.length) return () => {};

    const maskRects = items
      .slice(0, -1)
      .map((item) => item.querySelector(CONNECTOR_MASK_RECT_SELECTOR))
      .filter(Boolean);
    if (!maskRects.length) return () => {};

    const reduceMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      // Keep default state (no scrubbing) for reduced motion.
      return () => {};
    }

    let rafId = null;
    let running = false;

    const update = () => {
      rafId = null;
      if (!running) return;

      // Anchor line fill to a viewport reference point so it feels "scrubbed".
      const refY = window.innerHeight * 0.62;

      // One continuous progress for the entire steps list.
      // Sequence:
      //   step 1 pill -> connector 1 -> step 2 pill -> connector 2 -> ... -> last pill
      const firstRect = items[0].getBoundingClientRect();
      const lastRect = items[items.length - 1].getBoundingClientRect();

      const startY = firstRect.top + 28;
      const endY = lastRect.top + 28;
      const denom = Math.max(1, endY - startY);

      const totalPhases = items.length * 2 - 1;
      const phaseProgress = clamp01((refY - startY) / denom) * totalPhases;

      for (let i = 0; i < items.length; i += 1) {
        // Pill fill 0..1
        const pillFill = clamp01(phaseProgress - 2 * i);
        const pill = stepPills[i];
        if (pill) pill.style.setProperty(STEP_PILL_FILL_VAR, String(pillFill));

        // Connector fill 0..1 (for all but last)
        if (i < items.length - 1) {
          const connectorFill = clamp01(phaseProgress - (2 * i + 1));
          const rect = maskRects[i];
          const svg = rect?.ownerSVGElement;
          if (!rect || !svg) continue;
          const vb = svg.viewBox && svg.viewBox.baseVal ? svg.viewBox.baseVal : null;
          const vbHeight = vb && vb.height ? vb.height : 0;
          rect.setAttribute("x", "0");
          rect.setAttribute("y", "0");
          rect.setAttribute("width", "2");
          rect.setAttribute("height", (vbHeight * connectorFill).toFixed(2));
        }
      }
    };

    const requestUpdate = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(update);
    };

    const onScroll = () => requestUpdate();
    const onResize = () => requestUpdate();

    const start = () => {
      if (running) return;
      running = true;
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onResize);
      requestUpdate();
    };

    const stop = () => {
      running = false;
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (rafId != null) window.cancelAnimationFrame(rafId);
      rafId = null;
    };

    // Start/stop based on section visibility.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) start();
          else stop();
        });
      },
      { threshold: 0, rootMargin: "200px 0px 200px 0px" },
    );
    io.observe(sectionEl);

    return () => {
      stop();
      io.disconnect();
      stepPills.forEach((p) => p.style.removeProperty(STEP_PILL_FILL_VAR));
      maskRects.forEach((r) => r.setAttribute("height", "0"));
    };
  }

  function initBenefitsDraw(root = document, isDesignModeEvent = false) {
    const reduceMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const sections = getSectionsFromRoot(root);
    if (!sections.length) return;

    const reveal = (sectionEl) => {
      sectionEl.classList.add(SECTION_IN_VIEW_CLASS);
      sectionEl.dataset[DATA_DONE_ATTR] = "true";
    };

    sections.forEach((sectionEl) => {
      if (sectionEl.dataset[DATA_DONE_ATTR] === "true") return;

      // Always initialize: steps connector scrub and/or inline SVG icon animations.
      const teardownSteps = setupStepsScrub(sectionEl);

      if (reduceMotion || isDesignModeEvent || !("IntersectionObserver" in window)) {
        reveal(sectionEl);
        teardownSteps();
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            reveal(entry.target);
            io.unobserve(entry.target);
          });
        },
        { threshold: 0.4, rootMargin: "0px 0px -10% 0px" },
      );

      io.observe(sectionEl);
    });
  }

  window.addEventListener("DOMContentLoaded", () => initBenefitsDraw());

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener("shopify:section:load", (event) =>
      initBenefitsDraw(event.target, true),
    );
    document.addEventListener("shopify:section:reorder", () =>
      initBenefitsDraw(document, true),
    );
  }
})();

